import { useEffect, useState, useCallback } from "react";
import { TrendingUp, Gamepad2, Pizza, Smartphone, Banknote, Users, Ticket, Activity, MonitorSpeaker, AlertTriangle, Loader2, Zap, CalendarPlus, UserPlus, ReceiptText, ClipboardList } from "lucide-react";
import { api } from "@/lib/api";
import { toDateStr, fmtDate } from "@/lib/slots";

export default function OverviewTab() {
  const [stats, setStats] = useState(null);
  const [grid, setGrid] = useState(null);
  const [date, setDate] = useState(toDateStr(new Date()));
  const [slotDate, setSlotDate] = useState(toDateStr(new Date()));

  const load = useCallback(() => {
    api.get("/admin/stats", { params: { date } }).then((r) => setStats(r.data));
  }, [date]);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  const loadGrid = useCallback(() => {
    api.get("/admin/availability-grid", { params: { date: slotDate } }).then((r) => setGrid(r.data)).catch(() => setGrid(null));
  }, [slotDate]);
  useEffect(() => { loadGrid(); const t = setInterval(loadGrid, 20000); return () => clearInterval(t); }, [loadGrid]);

  if (!stats) return <Loader2 className="animate-spin text-purple-400" />;
  const sel = stats.selected;
  const go = (t) => window.dispatchEvent(new CustomEvent("psgc-nav", { detail: t }));
  const today = toDateStr(new Date());
  const yday = toDateStr(new Date(Date.now() - 86400000));
  const tmrw = toDateStr(new Date(Date.now() + 86400000));
  const rev = [
    { label: "Gaming", value: sel.gaming, icon: Gamepad2, c: "text-purple-300" },
    { label: "Food & Drinks", value: sel.food, icon: Pizza, c: "text-amber-300" },
    { label: "Online / PayU", value: sel.online, icon: Smartphone, c: "text-sky-300" },
    { label: "Cash", value: sel.cash, icon: Banknote, c: "text-emerald-300" },
  ];
  const mini = [
    { label: "Total Customers", value: stats.total_customers, icon: Users },
    { label: "Confirmed Bookings", value: stats.total_bookings, icon: Ticket },
    { label: "Active Sessions", value: stats.active_sessions, icon: Activity },
    { label: "Free Stations", value: stats.free_stations, icon: MonitorSpeaker },
  ];

  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-4" data-testid="quick-actions">
        <div className="flex items-center gap-2 mb-3"><Zap className="text-purple-300" size={18} /><h3 className="font-display font-bold text-sm uppercase tracking-wide">Quick Actions</h3></div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {[
            { label: "New Booking", icon: CalendarPlus, action: () => go("newbooking") },
            { label: "Walk-in", icon: UserPlus, action: () => go("live") },
            { label: "Live Monitor", icon: Activity, action: () => go("live") },
            { label: "Generate Bill", icon: ReceiptText, action: () => go("live") },
            { label: "Food Order", icon: ClipboardList, action: () => go("orders") },
          ].map((a) => (
            <button key={a.label} data-testid={`qa-${a.label.toLowerCase().replace(/ /g, "-")}`} onClick={a.action} className="flex flex-col items-center justify-center gap-1.5 h-20 rounded-xl glass border border-purple-500/30 hover:border-purple-400 hover:bg-purple-900/30 transition-all">
              <a.icon className="text-purple-300" size={20} /><span className="text-xs font-medium">{a.label}</span>
            </button>
          ))}
        </div>
      </div>

      {stats.low_stock > 0 && (
        <div className="glass rounded-2xl p-4 border border-amber-500/40 flex items-center gap-3" data-testid="low-stock-warning">
          <AlertTriangle className="text-amber-400 shrink-0" size={22} />
          <p className="text-sm text-amber-200"><span className="font-bold">{stats.low_stock} item{stats.low_stock > 1 ? "s" : ""} low in stock</span> — {stats.low_stock_items.map((i) => `${i.name} (${i.stock})`).join(", ")}</p>
        </div>
      )}

      <div className="glass rounded-3xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2"><TrendingUp className="text-purple-300" size={20} /><h3 className="font-display font-bold">Revenue — {sel.date === today ? "Today" : sel.date === yday ? "Yesterday" : fmtDate(sel.date)}</h3></div>
          <div className="flex gap-2 items-center">
            <button data-testid="rev-today" onClick={() => setDate(today)} className={`px-3 h-9 rounded-lg text-sm border ${date === today ? "bg-purple-600 border-purple-400" : "glass border-purple-500/30 text-slate-300"}`}>Today</button>
            <button data-testid="rev-yesterday" onClick={() => setDate(yday)} className={`px-3 h-9 rounded-lg text-sm border ${date === yday ? "bg-purple-600 border-purple-400" : "glass border-purple-500/30 text-slate-300"}`}>Yesterday</button>
            <input type="date" data-testid="rev-date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 rounded-lg bg-purple-950/30 border border-purple-500/30 px-2 text-sm text-slate-200 [color-scheme:dark]" />
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {rev.map((r) => (
            <div key={r.label} className="rounded-2xl bg-purple-950/30 border border-purple-500/20 p-4" data-testid={`rev-${r.label}`}>
              <r.icon className={`${r.c} mb-2`} size={20} />
              <p className="text-2xl font-bold">₹{r.value}</p>
              <p className="text-xs text-slate-400 uppercase tracking-wider mt-1">{r.label}</p>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-purple-500/10">
          <span className="text-sm text-slate-400 uppercase tracking-wider">{sel.bookings} confirmed booking(s)</span>
          <span className="font-display text-2xl font-bold text-emerald-400" data-testid="rev-total">Total ₹{sel.total}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {mini.map((c) => (
          <div key={c.label} className="glass rounded-2xl p-5" data-testid={`stat-${c.label}`}>
            <c.icon className="text-purple-300 mb-3" size={22} />
            <p className="text-2xl font-bold">{c.value}</p>
            <p className="text-xs text-slate-400 uppercase tracking-wider mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      {grid && (
        <div className="glass rounded-3xl p-5" data-testid="overview-slots">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="flex items-center gap-2"><MonitorSpeaker className="text-purple-300" size={20} /><h3 className="font-display font-bold">Slots Availability — {slotDate === today ? "Today" : slotDate === tmrw ? "Tomorrow" : fmtDate(slotDate)} ({grid.day_name})</h3></div>
            <div className="flex gap-2 items-center">
              <button data-testid="slots-today" onClick={() => setSlotDate(today)} className={`px-3 h-9 rounded-lg text-sm border ${slotDate === today ? "bg-purple-600 border-purple-400" : "glass border-purple-500/30 text-slate-300"}`}>Today</button>
              <button data-testid="slots-tomorrow" onClick={() => setSlotDate(tmrw)} className={`px-3 h-9 rounded-lg text-sm border ${slotDate === tmrw ? "bg-purple-600 border-purple-400" : "glass border-purple-500/30 text-slate-300"}`}>Tomorrow</button>
              <input type="date" data-testid="slots-date" value={slotDate} min={today} onChange={(e) => setSlotDate(e.target.value)} className="h-9 rounded-lg bg-purple-950/30 border border-purple-500/30 px-2 text-sm text-slate-200 [color-scheme:dark]" />
            </div>
          </div>
          <div className="overflow-auto max-h-96 rounded-xl border border-purple-500/10">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#160c2e]"><tr className="text-slate-300">
                <th className="text-left p-2">Time</th>
                {grid.types.map((t) => <th key={t.type} className="p-2">{t.label}</th>)}
                <th className="p-2">VR</th><th className="p-2">Steering</th>
              </tr></thead>
              <tbody>
                {grid.rows.map((r, i) => (
                  <tr key={i} className={i % 2 ? "bg-purple-950/20" : ""}>
                    <td className="p-2 font-mono text-slate-300 whitespace-nowrap">{r.start}–{r.end}</td>
                    {grid.types.map((t) => { const v = r.counts[t.type] || 0; return <td key={t.type} className={`p-2 text-center font-semibold ${v > 0 ? "text-emerald-400" : "text-red-400"}`}>{v > 0 ? `${v} free` : "busy"}</td>; })}
                    <td className={`p-2 text-center ${r.vr_free > 0 ? "text-emerald-400" : "text-red-400"}`}>{r.vr_free > 0 ? "free" : "busy"}</td>
                    <td className={`p-2 text-center ${r.steering_free > 0 ? "text-emerald-400" : "text-red-400"}`}>{r.steering_free > 0 ? "free" : "busy"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
